import { rem } from 'polished'
import { useMemo, type CSSProperties } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'

const ChartArea = styled(Group)`
  ${mediaQueryUp.tablet} {
    flex: 1 1 0;
    min-width: ${rem(450)};
  }
`

const Chart = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: ${rem(16)};
  width: 100%;
  height: ${rem(100)};
  background-image: linear-gradient(
    to bottom,
    ${props => props.theme.palette.neutral.default.six} 1px,
    transparent 1px,
    transparent calc(100% - 1px),
    ${props => props.theme.palette.neutral.default.six} calc(100% - 1px)
  );
  background-size: 100% ${rem(25)};
`

const PriceCallout = styled.div`
  position: absolute;
  top: calc(100% - var(--price-percentage));
  inset-inline-end: calc(100% + ${rem(12)});
  transform: translateY(-50%);
  padding: ${rem(4)} ${rem(10)};
  border-radius: ${props => props.theme.borderRadius.S};
  background-color: ${props => props.theme.palette.neutral.default.one};
  color: ${props => props.theme.palette.neutral.default.eight};
  white-space: nowrap;
`

const PriceConnector = styled.div`
  --price-connector-inline-offset: -100%;

  position: absolute;
  inset-inline-start: 0;
  bottom: calc(var(--price-percentage) - 1px);
  width: ${rem(12)};
  height: 1px;
  background-color: ${props => props.theme.palette.neutral.default.one};
  transform: translateX(var(--price-connector-inline-offset));

  [dir='rtl'] & {
    --price-connector-inline-offset: 100%;
  }
`

const OfferBarColumn = styled.div`
  position: relative;
  width: ${rem(80)};
  height: ${rem(100)};
`

const OfferBar = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: ${rem(80)};
  height: ${rem(100)};
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.L};
  background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
`

const SavingPortion = styled.div`
  flex: 0 0 var(--saving-percentage);
  background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
  background-image: repeating-linear-gradient(
    135deg,
    transparent 0,
    transparent ${rem(6)},
    ${props => props.theme.palette.highlight.primary.normalBackground} ${rem(6)},
    ${props => props.theme.palette.highlight.primary.normalBackground} ${rem(7)}
  );
`

const PricePortion = styled(Group)`
  flex: 0 0 var(--price-percentage);
  border-top: 1px solid ${props => props.theme.palette.neutral.default.one};
  background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
`

const ValueBar = styled(Group)`
  width: ${rem(80)};
  height: ${rem(100)};
  border-radius: ${props => props.theme.borderRadius.L};
  background-color: ${props => props.theme.palette.neutral.surface.lightForeground};
`

interface BarStyle extends CSSProperties {
  '--price-percentage': string
  '--saving-percentage': string
}

interface Props {
  price: number
  pricePercentage: number
  pricePercentageLabel: string
}

function FlashHotelValueComparisonVerticalBars(props: Props) {
  const { price, pricePercentage, pricePercentageLabel } = props
  const intl = useIntl()

  const barStyle = useMemo<BarStyle>(() => ({
    '--price-percentage': `${pricePercentage}%`,
    '--saving-percentage': `${100 - pricePercentage}%`,
  }), [pricePercentage])

  const valuePercentageLabel = intl.formatNumber(1, {
    style: 'percent',
    maximumFractionDigits: 0,
  })

  return (
    <ChartArea direction="vertical" gap={8}>
      <Chart>
        <OfferBarColumn>
          <PriceCallout style={barStyle} aria-hidden="true">
            <Caption variant="medium" weight="bold">
              <FormatCurrency value={price} format="roundedDollar" />
            </Caption>
          </PriceCallout>
          <PriceConnector style={barStyle} />
          <OfferBar style={barStyle}>
            <SavingPortion />
            <PricePortion direction="vertical" verticalAlign="center" horizontalAlign="center">
              <BodyText variant="medium" weight="bold">
                {pricePercentageLabel}
              </BodyText>
            </PricePortion>
          </OfferBar>
        </OfferBarColumn>
        <ValueBar direction="vertical" verticalAlign="center" horizontalAlign="center">
          <BodyText variant="medium" weight="bold">
            {valuePercentageLabel}
          </BodyText>
        </ValueBar>
      </Chart>
    </ChartArea>
  )
}

export default FlashHotelValueComparisonVerticalBars
